type PlotlyModule = typeof import('./plotlyCore')
type PlotlyInstance = PlotlyModule['default']
type PlotlyTraceLike = Record<string, unknown> & { type?: unknown }

const TRACE_MODULE_LOADERS = {
  scatter: () => import('plotly.js/lib/scatter'),
  bar: () => import('plotly.js/lib/bar'),
  box: () => import('plotly.js/lib/box'),
  heatmap: () => import('plotly.js/lib/heatmap'),
  histogram: () => import('plotly.js/lib/histogram'),
  histogram2d: () => import('plotly.js/lib/histogram2d'),
  histogram2dcontour: () => import('plotly.js/lib/histogram2dcontour'),
  contour: () => import('plotly.js/lib/contour'),
  pie: () => import('plotly.js/lib/pie'),
  violin: () => import('plotly.js/lib/violin'),
  parcoords: () => import('plotly.js/lib/parcoords'),
} as const

type TraceType = keyof typeof TRACE_MODULE_LOADERS

let plotlyPromise: Promise<PlotlyInstance> | null = null
let prefetchScheduled = false
const loadedTraceTypes = new Set<TraceType>()
const loadingTracePromises = new Map<TraceType, Promise<void>>()

const normalizeTraceType = (rawType: unknown): TraceType | null => {
  const normalized = String(rawType || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '')

  if (!normalized) return 'scatter'
  if (normalized === 'line' || normalized === 'lines' || normalized === 'area') {
    return 'scatter'
  }
  if (normalized in TRACE_MODULE_LOADERS) {
    return normalized as TraceType
  }
  return null
}

const collectTraceTypes = (plotData: unknown): TraceType[] => {
  const traces = Array.isArray(plotData) ? plotData : plotData ? [plotData] : []
  if (!traces.length) return []

  const types = new Set<TraceType>()
  traces.forEach((trace) => {
    if (!trace || typeof trace !== 'object') return
    const type = normalizeTraceType((trace as PlotlyTraceLike).type)
    if (type) types.add(type)
  })
  return Array.from(types)
}

const ensureTraceTypeLoaded = async (
  plotly: PlotlyInstance,
  traceType: TraceType
) => {
  if (loadedTraceTypes.has(traceType)) return

  const inFlight = loadingTracePromises.get(traceType)
  if (inFlight) {
    await inFlight
    return
  }

  const loadPromise = TRACE_MODULE_LOADERS[traceType]()
    .then((module) => {
      const traceModule = module?.default || module
      plotly.register([traceModule])
      loadedTraceTypes.add(traceType)
    })
    .finally(() => {
      loadingTracePromises.delete(traceType)
    })

  loadingTracePromises.set(traceType, loadPromise)
  await loadPromise
}

const ensurePlotlyTraceModules = async (
  plotly: PlotlyInstance,
  plotData: unknown
) => {
  const traceTypes = collectTraceTypes(plotData)
  if (!traceTypes.length) return
  await Promise.all(
    traceTypes.map((traceType) => ensureTraceTypeLoaded(plotly, traceType))
  )
}

const wrapPlotly = (plotly: PlotlyInstance): PlotlyInstance => {
  const wrapped = Object.create(plotly) as PlotlyInstance
  const wrappedAny = wrapped as unknown as {
    newPlot: (...args: unknown[]) => unknown
    react: (...args: unknown[]) => unknown
    plot: (...args: unknown[]) => unknown
  }
  const originalNewPlot =
    typeof plotly.newPlot === 'function' ? plotly.newPlot.bind(plotly) : null
  const originalReact =
    typeof plotly.react === 'function' ? plotly.react.bind(plotly) : null
  const originalPlot =
    typeof plotly.plot === 'function' ? plotly.plot.bind(plotly) : null

  wrappedAny.newPlot = async (...args: unknown[]) => {
    await ensurePlotlyTraceModules(plotly, args[1])
    if (!originalNewPlot) {
      throw new Error('Plotly newPlot is not available in current build')
    }
    return originalNewPlot(...(args as Parameters<typeof originalNewPlot>))
  }

  wrappedAny.react = async (...args: unknown[]) => {
    await ensurePlotlyTraceModules(plotly, args[1])
    if (originalReact) {
      return originalReact(...(args as Parameters<typeof originalReact>))
    }
    if (originalNewPlot) {
      return originalNewPlot(...(args as Parameters<typeof originalNewPlot>))
    }
    throw new Error('Plotly react/newPlot is not available in current build')
  }

  wrappedAny.plot = async (...args: unknown[]) => {
    await ensurePlotlyTraceModules(plotly, args[1])
    if (originalPlot) {
      return originalPlot(...(args as Parameters<typeof originalPlot>))
    }
    if (originalNewPlot) {
      return originalNewPlot(...(args as Parameters<typeof originalNewPlot>))
    }
    throw new Error('Plotly plot/newPlot is not available in current build')
  }

  return wrapped
}

const shouldSkipPrefetch = () => {
  if (typeof window === 'undefined') return false
  if (typeof document === 'object' && document.visibilityState === 'hidden') {
    return true
  }

  const nav = navigator as Navigator & {
    connection?: {
      saveData?: boolean
      effectiveType?: string
    }
  }
  const connection = nav.connection
  if (!connection) return false

  if (connection.saveData) return true

  const effectiveType = String(connection.effectiveType || '').toLowerCase()
  return effectiveType === 'slow-2g' || effectiveType === '2g'
}

export const loadPlotly = async () => {
  if (!plotlyPromise) {
    plotlyPromise = import('./plotlyCore').then((module) => {
      return wrapPlotly(module.default as PlotlyInstance)
    })
  }

  return plotlyPromise
}

export const prefetchPlotly = () => {
  if (plotlyPromise || prefetchScheduled || shouldSkipPrefetch()) return

  prefetchScheduled = true
  const runPrefetch = () => {
    prefetchScheduled = false
    void loadPlotly().catch(() => {
      // Silent prefetch failure: interactive load path still works.
    })
  }

  if (typeof window !== 'undefined') {
    const win = window as Window & {
      requestIdleCallback?: (
        callback: () => void,
        options?: { timeout: number }
      ) => number
    }
    const scheduleIdlePrefetch = () => {
      if (typeof win.requestIdleCallback === 'function') {
        win.requestIdleCallback(runPrefetch, { timeout: 2500 })
        return
      }
      setTimeout(runPrefetch, 0)
    }
    setTimeout(scheduleIdlePrefetch, 300)
    return
  }

  setTimeout(runPrefetch, 0)
}
